import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { PARK } from "@/lib/site";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { LuxeOverline } from "@/components/LuxeOverline";

const RELATED = [
  { to: "/lodges-lincolnshire", label: "Lakeside lodges in Lincolnshire" },
  { to: "/camping-lincolnshire", label: "Camping & touring in Lincolnshire" },
  { to: "/fishing-holidays-lincolnshire", label: "Carp fishing holidays" },
  { to: "/dog-friendly-holidays-lincolnshire", label: "Dog-friendly holidays" },
  { to: "/glamping-lincolnshire", label: "Glamping in Lincolnshire" },
  { to: "/pick-your-pitch", label: "Pick your exact pitch" },
];

/**
 * Shared layout for the keyword landing pages (/lodges-lincolnshire etc.).
 * Renders a slim header, a keyword-rich hero, page sections (children),
 * an FAQ block (mirrored into FAQPage structured data), internal links
 * and the site footer.
 */
const SeoLandingLayout = ({
  seo,
  overline,
  titlePre,
  titleEm,
  intro,
  heroImage,
  heroAlt,
  faqs = [],
  children,
}) => {
  const faqLd = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div className="bg-[#0A0A0A] text-[#EAE0D0] min-h-screen">
      <Seo {...seo} jsonLd={faqLd} />

      {/* Slim header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <Link to="/" aria-label="Walnut Lakes home" data-testid="seo-header-logo">
            <img src={PARK.logo} alt="Walnut Lakes Holiday Park" className="h-14 w-auto logo-blend" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#D8CDBA]">
            <Link to="/lodges-lincolnshire" className="link-underline" data-testid="seo-nav-lodges">Lodges</Link>
            <Link to="/camping-lincolnshire" className="link-underline" data-testid="seo-nav-camping">Camping</Link>
            <Link to="/fishing-holidays-lincolnshire" className="link-underline" data-testid="seo-nav-fishing">Fishing</Link>
            <Link to="/pick-your-pitch" className="link-underline" data-testid="seo-nav-map">Pick your pitch</Link>
          </nav>
          <Link
            to="/#book"
            className="btn-primary font-brand tracking-[0.14em] text-xs"
            data-testid="seo-header-book"
          >
            Book your stay
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14">
        <LuxeOverline>{overline}</LuxeOverline>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5EFE6] mt-5 max-w-3xl leading-[1.05]">
          {titlePre} <em className="text-[#E4B2A8]">{titleEm}</em>
        </h1>
        <p className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-[#D8CDBA]">{intro}</p>
        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/#book" className="btn-primary font-brand tracking-[0.14em]" data-testid="seo-hero-book">
            Check availability &amp; book
          </Link>
          <Link to="/pick-your-pitch" className="link-underline text-[#F5EFE6] inline-flex items-center gap-2" data-testid="seo-hero-map">
            Pick your exact pitch <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {heroImage && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <img
            src={heroImage}
            alt={heroAlt}
            className="w-full h-[320px] md:h-[460px] object-cover rounded-2xl"
            loading="lazy"
          />
        </section>
      )}

      {children}

      {/* FAQs */}
      {faqs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="seo-faq">
          <LuxeOverline>Good to know</LuxeOverline>
          <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
            Frequently asked questions
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-10">
            {faqs.map((f) => (
              <div key={f.q}>
                <h3 className="font-serif text-lg text-[#F5EFE6]">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#D8CDBA]">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Internal links + contact strip */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <LuxeOverline>Explore Walnut Lakes</LuxeOverline>
            <ul className="mt-6 space-y-3">
              {RELATED.map((r) => (
                <li key={r.to}>
                  <Link to={r.to} className="link-underline text-[#F5EFE6] inline-flex items-center gap-2" data-testid={`seo-related-${r.to.slice(1)}`}>
                    {r.label} <ArrowRight size={14} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <LuxeOverline>Talk to a human</LuxeOverline>
            <p className="mt-6 text-sm leading-relaxed text-[#D8CDBA] max-w-md">
              We&apos;re a small family-run park — replies come from a real person,
              usually within 24 hours during the season.
            </p>
            <a
              href={`tel:${PARK.phoneRaw}`}
              className="mt-5 inline-flex items-center gap-2 text-[#F5EFE6] link-underline"
              data-testid="seo-contact-phone"
            >
              <Phone size={15} /> {PARK.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeoLandingLayout;
